import React from 'react';
import PropTypes from 'prop-types';
import { MultiColumnList, Icon, Loading } from '@folio/stripes/components';
import { FormattedMessage, FormattedDate, FormattedTime } from 'react-intl';

function ExecutionHistoryTable({ executions, isLoading }) {
  if (isLoading) {
    return <Loading />;
  }

  if (!executions || executions.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
        <FormattedMessage
          id="ui-reports.executionHistory.empty"
          defaultMessage="No execution history available"
        />
      </div>
    );
  }

  const STATUS_STYLES = {
    success: {
      background: '#d1e7dd',
      color:      '#0a3622',
      border:     '1px solid #a3cfbb',
    },
    failed: {
      background: '#f8d7da',
      color:      '#58151c',
      border:     '1px solid #f1aeb5',
    },
    running: {
      background: '#cfe2ff',
      color:      '#052c65',
      border:     '1px solid #9ec5fe',
    },
  };

  const getStatusBadge = (status) => {
    const style = STATUS_STYLES[status] || STATUS_STYLES.running;
    return (
      <span style={{
        ...style,
        display:      'inline-block',
        padding:      '0.2em 0.6em',
        borderRadius: '0.25rem',
        fontSize:     '0.8rem',
        fontWeight:   '600',
        whiteSpace:   'nowrap',
      }}>
        <FormattedMessage
          id={`ui-reports.executionHistory.status.${status}`}
          defaultMessage={status}
        />
      </span>
    );
  };

  const formatter = {
    executionDate: (item) => (
      <div>
        <div><FormattedDate value={item.executionDate} /></div>
        <div style={{ fontSize: '0.85rem', color: '#666' }}>
          <FormattedTime value={item.executionDate} />
        </div>
      </div>
    ),
    status: (item) => getStatusBadge(item.status),
    recordsCount: (item) => (
      <span style={{ fontWeight: item.recordsCount > 0 ? 'bold' : 'normal' }}>
        {item.recordsCount != null ? item.recordsCount.toLocaleString() : '—'}
      </span>
    ),
    duration: (item) => {
      if (item.duration) return `${Math.floor(item.duration / 1000)}s`;
      return '-';
    },
    emailSent: (item) => {
      if (item.emailSent === true) {
        return (
          <span style={{ color: 'var(--color-success, #198754)', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
            <Icon icon="check-circle" size="small" />
            <FormattedMessage id="ui-reports.executionHistory.emailSent.yes" defaultMessage="Sent" />
          </span>
        );
      }
      if (item.emailSent === false) {
        return (
          <span style={{ color: 'var(--color-error, #dc3545)', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
            <Icon icon="times-circle" size="small" />
            <FormattedMessage id="ui-reports.executionHistory.emailSent.no" defaultMessage="Not sent" />
          </span>
        );
      }
      return <span style={{ color: '#aaa' }}>—</span>;
    },
    errorMessage: (item) => (
      <div style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {item.errorMessage || '-'}
      </div>
    ),
  };

  return (
    <MultiColumnList
      id="execution-history-list"
      contentData={executions}
      visibleColumns={['executionDate', 'status', 'recordsCount', 'duration', 'emailSent', 'errorMessage']}
      columnWidths={{
        executionDate: '20%',
        status:        '10%',
        recordsCount:  '12%',
        duration:      '8%',
        emailSent:     '12%',
        errorMessage:  '38%',
      }}
      columnMapping={{
        executionDate: <FormattedMessage id="ui-reports.executionHistory.date" defaultMessage="Execution Date" />,
        status:        <FormattedMessage id="ui-reports.executionHistory.status" defaultMessage="Status" />,
        recordsCount:  <FormattedMessage id="ui-reports.executionHistory.records" defaultMessage="Records" />,
        duration:      <FormattedMessage id="ui-reports.executionHistory.duration" defaultMessage="Duration" />,
        emailSent:     <FormattedMessage id="ui-reports.executionHistory.emailSent" defaultMessage="Email" />,
        errorMessage:  <FormattedMessage id="ui-reports.executionHistory.error" defaultMessage="Error Message" />,
      }}
      formatter={formatter}
      interactive={false}
    />
  );
}

ExecutionHistoryTable.propTypes = {
  executions: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool
};

ExecutionHistoryTable.defaultProps = {
  executions: [],
  isLoading: false
};

export default ExecutionHistoryTable;
